import {inject, injectable} from 'inversify'
import {action, makeObservable, observable, runInAction} from 'mobx'

import {DI} from '@/src/config/di/types'
import type {FavouriteRepositoryInterface, ToggleFavouriteUseCase} from '@/src/core/application/favourite'
import type {INotificationService} from '@/src/ports/services/notification'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import type {IEventBus} from '@/src/core/domain/events'
import {FavouriteEvents} from '@/src/core/domain/events'
import type {FavouriteProductId} from '@/src/features/favourite/core/types'
import {FavouriteStoreInterface} from './favourite.store.interface'

/**
 * Favourite Store - Presentation Layer
 *
 * Responsible for:
 * - UI state management (favouriteIds, pending)
 * - Delegating business operations to use cases
 * - User notifications via INotificationService
 *
 * This store follows SRP: it only manages UI state and orchestrates use cases.
 * Business logic is handled by ToggleFavouriteUseCase.
 */
@injectable()
export class FavouriteStore implements FavouriteStoreInterface {
  @observable
  pending = false

  @observable
  favouriteIds: FavouriteProductId[] = []

  constructor(
    @inject(DI.FavouriteRepositoryInterface) private readonly repository: FavouriteRepositoryInterface,
    @inject(DI.ToggleFavouriteUseCase) private readonly toggleFavouriteUseCase: ToggleFavouriteUseCase,
    @inject(DI.INotificationService) private readonly notifications: INotificationService,
    @inject(DI.IErrorReporter) private readonly errorReporter: IErrorReporter,
    @inject(DI.IEventBus) private readonly eventBus: IEventBus,
  ) {
    makeObservable(this)
  }

  @action.bound
  async load(): Promise<void> {
    try {
      const favouriteIds = await this.repository.getAll()
      runInAction(() => {
        this.favouriteIds = favouriteIds
      })

      // Publish domain event
      this.eventBus.publish(
        FavouriteEvents.listLoaded({
          productIds: favouriteIds,
        }),
      )
    } catch (e) {
      this.errorReporter.capture(e instanceof Error ? e : new Error(String(e)))
    }
  }

  is(id: Id): boolean {
    return this.favouriteIds.includes(id)
  }

  @action.bound
  private setPending(pending: boolean): void {
    this.pending = pending
  }

  /**
   * Toggle favourite status for a product
   * Delegates to ToggleFavouriteUseCase
   */
  @action.bound
  async toggle(id: Id): Promise<void> {
    this.setPending(true)

    // Optimistic update for better UX
    const wasInFavourites = this.is(id)
    const previousIds = [...this.favouriteIds]

    runInAction(() => {
      if (wasInFavourites) {
        this.favouriteIds = this.favouriteIds.filter(fid => fid !== id)
      } else {
        this.favouriteIds = [...this.favouriteIds, id]
      }
    })

    const result = await this.toggleFavouriteUseCase.execute(id, previousIds)

    runInAction(() => {
      if (result.isOk()) {
        // Update with confirmed state from use case
        this.favouriteIds = result.value.updatedIds

        if (result.value.isNowFavourite) {
          this.notifications.success('Товар добавлен в избранное')


          // Publish domain event
          this.eventBus.publish(
            FavouriteEvents.productAdded({productId: id}),
          )
        } else {
          this.notifications.success('Товар удален из избранного')

          // Publish domain event
          this.eventBus.publish(
            FavouriteEvents.productRemoved({productId: id}),
          )
        }
      } else {
        // Rollback optimistic update on error
        this.favouriteIds = previousIds
        this.notifications.error('Не удалось обновить избранное')
      }

      this.setPending(false)
    })
  }
}
